import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  CheckCircle2,
  Shield,
  Clock,
  Briefcase,
  CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import AnimatedGradientText from "@/components/AnimatedGradientText";
import supabase from "../utils/supabase.ts";
import { z } from "zod";

const waitlistSchema = z.object({
  fullName: z.string().min(2, "Name must be at least 2 characters long").nonempty("Full name is required"),
  email: z.string().email("Please enter a valid email address").nonempty("Email is required"),
  phone: z
    .string()
    .regex(/^\+91[6-9][0-9]{9}$/, "Please enter a valid Indian phone number (+91 followed by 10 digits starting with 6-9)")
    .nonempty("Phone number is required"),
  occupation: z.string().nonempty("Occupation is required"),
  previousCommittee: z.enum(["yes", "no"]),
  committeeAmount: z
    .string()
    .optional()
    .refine(
      (val) => !val || (Number(val) > 0 && !isNaN(Number(val))),
      "Amount must be greater than 0"
    ),
  reason: z.string().min(10, "Please provide a more detailed reason").nonempty("Please tell us why you're interested"),
}).refine(
  (data) => data.previousCommittee === "no" || (data.previousCommittee === "yes" && !!data.committeeAmount),
  {
    message: "Please enter the committee amount",
    path: ["committeeAmount"],
  }
);

type FormData = z.infer<typeof waitlistSchema>;
type FormErrors = Partial<Record<keyof FormData, string>>;

const Waitlist = () => {
  const [formData, setFormData] = useState<FormData>({
    fullName: "",
    email: "",
    phone: "+91",
    occupation: "",
    previousCommittee: "no",
    committeeAmount: "",
    reason: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
  
    if (name === "phone") {
      let phoneValue = value;
  
      // Allow clearing the field
      if (phoneValue === "" || phoneValue === "+") {
        setFormData((prev) => ({ ...prev, [name]: "" }));
        return;
      }
  
      // Ensure it starts with +91 only when typing
      if (!phoneValue.startsWith("+91")) {
        phoneValue = "+91";
      }
  
      // Extract only the numeric part after +91
      const numericPart = phoneValue.replace(/\D/g, "").slice(2, 12); // Exclude "+91" and limit 10 digits
  
      // If numeric part is empty, allow "+91" only (prevent extra '9' issue)
      if (numericPart.length === 0) {
        phoneValue = "+91";
      } else {
        phoneValue = "+91" + numericPart;
      }
  
      setFormData((prev) => ({ ...prev, [name]: phoneValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const result = waitlistSchema.safeParse(formData);
    if (!result.success) {
      const newErrors: FormErrors = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as keyof FormErrors;
        newErrors[field] = err.message;
      });
      setErrors(newErrors);
      toast.error("Please fix the errors in the form");
      return;
    }

    setIsSubmitting(true);

    try {
      const dataToInsert = {
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        occupation: formData.occupation,
        experience: formData.previousCommittee === "yes",
        amount: formData.previousCommittee === "yes" ? parseFloat(formData.committeeAmount || "0") || null : null,
        message: formData.reason,
      };

      const { error } = await supabase.from("join_waitlist").insert([dataToInsert]);

      if (error) {
        throw new Error(error.message);
      }

      setIsSubmitted(true);
      toast.success("You've been added to our waitlist!", {
        description: "We'll notify you when we launch.",
      });
    } catch (error: Error | unknown) {
      toast.error("Something went wrong", {
        description: error instanceof Error ? error.message : "Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 py-12 pt-8">
        <Link
          to="/"
          className="inline-flex items-center text-sm font-medium text-primary mb-8 hover:underline transition-colors"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Home
        </Link>

        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8 backdrop-blur-sm backdrop-filter">
          {!isSubmitted ? (
            <>
              <div className="flex flex-wrap gap-3 mb-8">
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-amber-50/80 border border-amber-200 text-amber-700 text-sm font-medium transition-all hover:bg-amber-100">
                  <Clock size={14} className="mr-2" />
                  Launching Soon
                </div>
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-50/80 border border-blue-200 text-blue-700 text-sm font-medium transition-all hover:bg-blue-100">
                  <Shield size={14} className="mr-2" />
                  100% KYC Verified
                </div>
              </div>

              <h1 className="text-4xl font-display font-bold mb-3">
                Join Our{" "}
                <AnimatedGradientText as="span">Waitlist</AnimatedGradientText>
              </h1>
              <p className="text-foreground/70 mb-8 text-lg">
                Be among the first to experience our secure digital committee platform.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      placeholder="Enter your full name"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className={`mt-2 ${errors.fullName ? "border-red-500" : ""}`}
                    />
                    {errors.fullName && (
                      <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={`mt-2 ${errors.email ? "border-red-500" : ""}`}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      placeholder="+91 9876543210"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className={`mt-2 ${errors.phone ? "border-red-500" : ""}`}
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="occupation">Occupation</Label>
                    <Input
                      id="occupation"
                      name="occupation"
                      placeholder="Your current occupation"
                      value={formData.occupation}
                      onChange={handleChange}
                      required
                      className={`mt-2 ${errors.occupation ? "border-red-500" : ""}`}
                    />
                    {errors.occupation && (
                      <p className="text-red-500 text-sm mt-1">{errors.occupation}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="previousCommittee">Previous Committee Experience?</Label>
                    <select
                      id="previousCommittee"
                      name="previousCommittee"
                      value={formData.previousCommittee}
                      onChange={handleChange}
                      className="w-full mt-2 px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>

                  {formData.previousCommittee === "yes" && (
                    <div>
                      <Label htmlFor="committeeAmount">Previous Committee Amount</Label>
                      <Input
                        id="committeeAmount"
                        name="committeeAmount"
                        placeholder="Amount in INR"
                        value={formData.committeeAmount}
                        onChange={handleChange}
                        type="number"
                        min="0"
                        className={`mt-2 ${errors.committeeAmount ? "border-red-500" : ""}`}
                      />
                      {errors.committeeAmount && (
                        <p className="text-red-500 text-sm mt-1">{errors.committeeAmount}</p>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="reason">Why are you interested?</Label>
                  <textarea
                    id="reason"
                    name="reason"
                    placeholder="Tell us why you're interested in our platform"
                    value={formData.reason}
                    onChange={handleChange}
                    className={`w-full min-h-[120px] px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary mt-2 resize-none ${
                      errors.reason ? "border-red-500" : ""
                    }`}
                  />
                  {errors.reason && (
                    <p className="text-red-500 text-sm mt-1">{errors.reason}</p>
                  )}
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    className="w-full py-6 text-base font-medium rounded-xl transition-all hover:scale-[1.02]"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Joining..." : "Join Waitlist"}
                  </Button>
                  <p className="text-sm text-center text-foreground/60 mt-4">
                    By joining, you agree to our Terms of Service and Privacy Policy.
                  </p>
                </div>
              </form>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 text-green-600 mb-6">
                <CheckCircle2 size={40} />
              </div>
              <h2 className="text-3xl font-display font-bold mb-4">
                You're on the list!
              </h2>
              <p className="text-foreground/70 mb-8 text-lg max-w-md mx-auto">
                Thank you for joining our waitlist. We'll notify you as soon as we launch.
                Meanwhile, feel free to share with your friends!
              </p>
              <Link to="/">
                <Button className="px-8 py-6 rounded-xl">Back to Home</Button>
              </Link>
            </div>
          )}
        </div>

        <div className="mt-12 text-center text-sm text-foreground/70 max-w-lg mx-auto bg-white/50 p-8 rounded-xl">
          <h3 className="font-semibold text-foreground text-xl mb-6">
            Why join our waitlist?
          </h3>
          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <Briefcase className="w-5 h-5 text-primary mt-1" />
              <div className="text-left">
                <p className="font-medium">Early Access</p>
                <p>Be the first to use our platform</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CreditCard className="w-5 h-5 text-primary mt-1" />
              <div className="text-left">
                <p className="font-medium">Zero Fees</p>
                <p>Special benefits for early adopters</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Shield className="w-5 h-5 text-primary mt-1" />
              <div className="text-left">
                <p className="font-medium">100% Secure</p>
                <p>KYC verified users only</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Clock className="w-5 h-5 text-primary mt-1" />
              <div className="text-left">
                <p className="font-medium">Priority Support</p>
                <p>Dedicated assistance</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Waitlist;